import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users, UserPlus, Loader2, Mail, ShieldCheck, AlertTriangle, User, Package, Settings2, Crown, FolderKanban
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TeamPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });

  // Manage Access modal state
  const [accessMember, setAccessMember] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);

  // Manage Projects modal state
  const [projectMember, setProjectMember] = useState(null);
  const [allProjectsMode, setAllProjectsMode] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [savingProjects, setSavingProjects] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/auth/team`, { headers });
      setTeam(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team');
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      toast.error('All fields are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/auth/create-team-member`, form, { headers });
      toast.success('Team member created successfully');
      setForm({ email: '', password: '', first_name: '', last_name: '' });
      setShowForm(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team member');
    }
    setCreating(false);
  };

  const configTools = team?.product_config?.tools || [];
  const configKeys = configTools.map(t => t.key);
  const isPrimaryViewer = team?.viewer_is_primary;

  const openAccessModal = (member) => {
    setAccessMember(member);
    setSelectedTools(member.allowed_tools === null || member.allowed_tools === undefined
      ? [...configKeys]
      : member.allowed_tools.filter(k => configKeys.includes(k)));
  };

  const toggleTool = (key) => {
    setSelectedTools(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const saveAccess = async () => {
    setSavingAccess(true);
    try {
      const allSelected = selectedTools.length === configKeys.length;
      await axios.put(`${API}/api/auth/team/${accessMember.id}/tools`, {
        allowed_tools: allSelected ? null : selectedTools
      }, { headers });
      toast.success(`Product access updated for ${accessMember.first_name}`);
      setAccessMember(null);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    }
    setSavingAccess(false);
  };

  const accessSummary = (member) => {
    if (member.is_primary) return 'All tools · Primary';
    if (member.allowed_tools === null || member.allowed_tools === undefined) return 'All tools';
    const count = member.allowed_tools.filter(k => configKeys.includes(k)).length;
    return `${count} of ${configKeys.length} tools`;
  };

  const companyProjects = team?.projects || [];
  const projectIds = companyProjects.map(p => p.id);

  const projectSummary = (member) => {
    if (member.is_primary) return 'All projects';
    if (member.allowed_projects === null || member.allowed_projects === undefined) return 'All projects';
    const count = member.allowed_projects.filter(id => projectIds.includes(id)).length;
    return `${count} of ${projectIds.length} projects`;
  };

  const openProjectsModal = (member) => {
    setProjectMember(member);
    const unrestricted = member.allowed_projects === null || member.allowed_projects === undefined;
    setAllProjectsMode(unrestricted);
    setSelectedProjects(unrestricted ? [...projectIds] : member.allowed_projects.filter(id => projectIds.includes(id)));
  };

  const toggleProject = (id) => {
    setSelectedProjects(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const saveProjects = async () => {
    setSavingProjects(true);
    try {
      await axios.put(`${API}/api/auth/team/${projectMember.id}/projects`, {
        allowed_projects: allProjectsMode ? null : selectedProjects
      }, { headers });
      toast.success(`Project access updated for ${projectMember.first_name}`);
      setProjectMember(null);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update project access');
    }
    setSavingProjects(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-center py-20 text-slate-500">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
        <p>You must be associated with a company to manage team members.</p>
      </div>
    );
  }

  const canAdd = team.active_users < team.max_users;
  const seatsUsed = team.active_users;
  const seatsTotal = team.max_users;

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Team Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">{team.company}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-700">{seatsUsed} of {seatsTotal} seats used</p>
            <div className="w-32 h-2 bg-slate-200 rounded-full mt-1">
              <div
                className={`h-full rounded-full transition-all ${seatsUsed >= seatsTotal ? 'bg-red-500' : seatsUsed >= seatsTotal - 1 ? 'bg-amber-500' : 'bg-teal-500'}`}
                style={{ width: `${Math.min((seatsUsed / seatsTotal) * 100, 100)}%` }}
              />
            </div>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            disabled={!canAdd}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-team-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            Add Team Member
          </Button>
        </div>
      </div>

      {/* Product Configuration */}
      <Card className="border-violet-200 bg-violet-50/30" data-testid="product-config-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Package className="w-5 h-5 text-violet-600" />
            Product Configuration
            {team.product_config?.config_name
              ? <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">{team.product_config.config_name}</Badge>
              : <Badge variant="outline" className="text-slate-500">All tools — no configuration assigned</Badge>}
          </CardTitle>
          <CardDescription>
            Products licensed to your company{isPrimaryViewer ? '. As the Primary user, you can allocate these to individual team members below.' : '. The Primary user manages who can access each product.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {configTools.map(t => (
              <Badge key={t.key} variant="secondary" className="bg-white border border-violet-200 text-violet-800" data-testid={`config-tool-${t.key}`}>
                {t.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {!canAdd && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            Your company has reached its license limit of {seatsTotal} user(s). Contact your administrator to increase the limit.
          </p>
        </div>
      )}

      {/* Add Team Member Form */}
      {showForm && canAdd && (
        <Card className="border-teal-200 bg-teal-50/30" data-testid="add-member-form">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserPlus className="w-5 h-5 inline mr-2 text-teal-600" />
              New Team Member
            </CardTitle>
            <CardDescription>Create a user account for your company. They will be asked to change their password on first login.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="Jane"
                  data-testid="team-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Smith"
                  data-testid="team-last-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="jane.smith@company.com"
                  data-testid="team-email"
                />
                <p className="text-xs text-slate-500">Shared addresses (info@, sales@, support@) are not allowed.</p>
              </div>
              <div className="space-y-2">
                <Label>Temporary Password</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Min. 6 characters"
                  data-testid="team-password"
                />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" disabled={creating} className="bg-teal-600 hover:bg-teal-700" data-testid="create-member-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
                  {creating ? 'Creating...' : 'Create Team Member'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Team Members List */}
      <Card className="border-slate-200" data-testid="team-members-list">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-5 h-5 text-teal-600" />
            Team Members
            <Badge variant="secondary">{team.total_users}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {team.users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <User className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>No team members yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {team.users.map(member => (
                <div key={member.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100" data-testid={`team-member-${member.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-semibold text-sm">
                      {member.first_name?.[0]?.toUpperCase()}{member.last_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {member.first_name} {member.last_name}
                        {member.is_primary && (
                          <Badge className="bg-violet-600 hover:bg-violet-600 text-white gap-1 text-[10px] px-1.5" data-testid={`primary-badge-${member.id}`}>
                            <Crown className="w-3 h-3" />Primary
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Mail className="w-3 h-3" />
                        {member.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-slate-600" data-testid={`access-summary-${member.id}`}>
                      <Package className="w-3 h-3 mr-1" />{accessSummary(member)}
                    </Badge>
                    <Badge variant="outline" className="text-xs text-slate-600" data-testid={`project-summary-${member.id}`}>
                      <FolderKanban className="w-3 h-3 mr-1" />{projectSummary(member)}
                    </Badge>
                    {member.mfa_enabled && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <ShieldCheck className="w-3 h-3" />MFA
                      </Badge>
                    )}
                    <Badge variant={member.active !== false ? 'default' : 'secondary'} className={member.active !== false ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                      {member.active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                    {isPrimaryViewer && !member.is_primary && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openAccessModal(member)} data-testid={`manage-access-${member.id}`}>
                          <Settings2 className="w-3.5 h-3.5 mr-1.5" />Manage Access
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openProjectsModal(member)} data-testid={`manage-projects-${member.id}`}>
                          <FolderKanban className="w-3.5 h-3.5 mr-1.5" />Manage Projects
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manage Access Modal */}
      <Dialog open={!!accessMember} onOpenChange={(open) => { if (!open) setAccessMember(null); }}>
        <DialogContent className="max-w-md" data-testid="manage-access-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Manage Product Access
            </DialogTitle>
            <DialogDescription>
              Select which licensed products {accessMember?.first_name} {accessMember?.last_name} can access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {configTools.map(t => (
              <label
                key={t.key}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                data-testid={`access-tool-${t.key}`}
              >
                <Checkbox
                  checked={selectedTools.includes(t.key)}
                  onCheckedChange={() => toggleTool(t.key)}
                  data-testid={`access-checkbox-${t.key}`}
                />
                <span className="text-sm font-medium text-slate-700">{t.label}</span>
              </label>
            ))}
            {configTools.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No products in your company's configuration.</p>
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{selectedTools.length} of {configKeys.length} selected</span>
            <div className="flex gap-2">
              <button type="button" className="text-violet-600 hover:underline" onClick={() => setSelectedTools([...configKeys])}>Select all</button>
              <button type="button" className="text-violet-600 hover:underline" onClick={() => setSelectedTools([])}>Clear</button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessMember(null)}>Cancel</Button>
            <Button onClick={saveAccess} disabled={savingAccess} className="bg-teal-600 hover:bg-teal-700" data-testid="save-access-btn">
              {savingAccess ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Projects Modal */}
      <Dialog open={!!projectMember} onOpenChange={(open) => { if (!open) setProjectMember(null); }}>
        <DialogContent className="max-w-md" data-testid="manage-projects-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Manage Project Access
            </DialogTitle>
            <DialogDescription>
              Select which projects {projectMember?.first_name} {projectMember?.last_name} can access.
            </DialogDescription>
          </DialogHeader>
          <label
            className="flex items-center gap-3 p-3 rounded-lg border border-violet-200 bg-violet-50/50 hover:bg-violet-50 cursor-pointer"
            data-testid="all-projects-toggle"
          >
            <Checkbox
              checked={allProjectsMode}
              onCheckedChange={(checked) => {
                setAllProjectsMode(!!checked);
                if (checked) setSelectedProjects([...projectIds]);
              }}
              data-testid="all-projects-checkbox"
            />
            <div>
              <span className="text-sm font-medium text-slate-700">All projects</span>
              <p className="text-xs text-slate-500">Includes any new projects created in future</p>
            </div>
          </label>
          <div className="space-y-2 py-1 max-h-64 overflow-y-auto">
            {companyProjects.map(p => (
              <label
                key={p.id}
                className={`flex items-center gap-3 p-3 rounded-lg border border-slate-200 ${allProjectsMode ? 'opacity-50 pointer-events-none' : 'hover:bg-slate-50 cursor-pointer'}`}
                data-testid={`access-project-${p.id}`}
              >
                <Checkbox
                  checked={selectedProjects.includes(p.id)}
                  disabled={allProjectsMode}
                  onCheckedChange={() => toggleProject(p.id)}
                  data-testid={`project-checkbox-${p.id}`}
                />
                <span className="text-sm font-medium text-slate-700">{p.title}</span>
              </label>
            ))}
            {companyProjects.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">Your company has no projects yet.</p>
            )}
          </div>
          {!allProjectsMode && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{selectedProjects.length} of {projectIds.length} selected</span>
              <div className="flex gap-2">
                <button type="button" className="text-violet-600 hover:underline" onClick={() => setSelectedProjects([...projectIds])}>Select all</button>
                <button type="button" className="text-violet-600 hover:underline" onClick={() => setSelectedProjects([])}>Clear</button>
              </div>
            </div>
          )}
          {!allProjectsMode && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              Restricted members will not see new projects until you grant access. They also cannot create or import projects.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectMember(null)}>Cancel</Button>
            <Button onClick={saveProjects} disabled={savingProjects} className="bg-teal-600 hover:bg-teal-700" data-testid="save-projects-btn">
              {savingProjects ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
